import { Env } from '@/Env';
import { Logtail } from '@logtail/browser';
import { LogIdentifierType } from '@/integrations/betterstack/enums/LogIndentifierType';
import LogHelper from '@/integrations/betterstack/LogHelper';
import { AuthUser } from '@/modules/auth/models/AuthUser';
import { getters } from '@/modules/core/app/helpers/store';

class LogService {
  private logtail: Logtail | null = null;

  init(): void {
    if (!this.isEnabled) {
      return;
    }

    this.logtail = new Logtail(this.getSourceToken, {
      endpoint: `https://${this.getIngestingHost}`
    });
  }

  info(message: string, type: LogIdentifierType, meta?: Record<string, unknown>): void {
    if (!this.isEnabled || !this.logtail) {
      return;
    }

    LogHelper.info(this.logtail, message, { ...this.defaultMeta(type), meta });
    LogHelper.flush(this.logtail);
  }

  error(message: string, type: LogIdentifierType, meta?: Record<string, unknown>): void {
    if (!this.isEnabled || !this.logtail) {
      return;
    }

    LogHelper.error(this.logtail, message, { ...this.defaultMeta(type), meta });
    LogHelper.flush(this.logtail);
  }

  debug(message: string, type: LogIdentifierType, meta?: Record<string, unknown>): void {
    if (!this.isEnabled || !this.logtail) {
      return;
    }

    LogHelper.debug(this.logtail, message, { ...this.defaultMeta(type), meta });
    LogHelper.flush(this.logtail);
  }

  warn(message: string, type: LogIdentifierType, meta?: Record<string, unknown>): void {
    if (!this.isEnabled || !this.logtail) {
      return;
    }

    LogHelper.warn(this.logtail, message, { ...this.defaultMeta(type), meta });
    LogHelper.flush(this.logtail);
  }

  log(
    message: string,
    type: LogIdentifierType,
    logLevel: string,
    meta?: Record<string, unknown>
  ): void {
    if (!this.isEnabled || !this.logtail) {
      return;
    }

    LogHelper.log(this.logtail, message, logLevel, { ...this.defaultMeta(type), meta });
    LogHelper.flush(this.logtail);
  }

  get getSourceToken(): string {
    return Env.nodeEnv().VITE_APP_LOGTAIL_SOURCE_TOKEN as string;
  }

  get getIngestingHost(): string {
    return Env.nodeEnv().VITE_APP_LOGTAIL_INGESTING_HOST as string;
  }

  get isEnabled(): boolean {
    return Env.nodeEnv().LOG_ENABLED && Env.isProduction() && !Env.isRunningCypress();
  }

  defaultMeta(type: LogIdentifierType): Record<string, unknown> {
    const user: AuthUser | null = getters.auth.getUser();

    return {
      url: Env.isBrowser() ? window.location.href : '',
      user,
      type
    };
  }
}

export default new LogService();
